import {
  MakeKavaPremiumMember,
  MakeKavaPremiumMemberVariables,
} from "_graphql/visal/mutations/__generated__/MakeKavaPremiumMember";
import {
  RemoveReinsurer,
  RemoveReinsurerVariables,
} from "_graphql/mutation/__generated__/RemoveReinsurer";
import {
  RevokeKavaPremiumMember,
  RevokeKavaPremiumMemberVariables,
} from "_graphql/visal/mutations/__generated__/RevokeKavaPremiumMember";
import {
  makeKavaPremiumMember,
  removeReInsurer,
  revokeKavaPremiumMember,
} from "_graphql/visal/mutations/re-insurer";

import { LocationGenerics } from "router/location";
import { PopUp } from "components";
import toast from "react-hot-toast";
import { useMutation } from "@apollo/client";
import { useReInsurer } from "hooks/data/use-re-insurers";
import { useSearch } from "react-location";

export default function KavaPremiumContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const { reInsurer, loading: fetching } = useReInsurer(searchParams?.id || "");
  const [remove, { loading }] = useMutation<
    RevokeKavaPremiumMember,
    RevokeKavaPremiumMemberVariables
  >(revokeKavaPremiumMember, {
    refetchQueries: ["Reinsurers"],
  });
  const [add, { loading: adding }] = useMutation<
    MakeKavaPremiumMember,
    MakeKavaPremiumMemberVariables
  >(makeKavaPremiumMember, {
    refetchQueries: ["Reinsurers"],
  });

  const isPremiumMember =
    reInsurer?.kava_premium_member?.membership_status == "ACTIVE";

  const handleSubmit = async () => {
    if (!isPremiumMember) {
      await add({
        variables: {
          reinsurerId: searchParams?.id || "",
        },
      })
        .then(({ data }) => {
          if (data?.makeKavaPremiumMember) {
            toast.success("Re-insurer added to Premium Membership");
            refetch?.();
            setOpen?.(false);
          } else {
            toast.error("Failed to add Re-insurer to Premium Membership");
          }
        })
        .catch((err) => {
          toast.error(
            err?.message || "Failed to add Re-insurer to Premium Membership"
          );
        });
      return;
    } else {
      await remove({
        variables: {
          reinsurerId: searchParams?.id || "",
        },
      })
        .then(({ data }) => {
          if (data?.revokeKavaPremiumMember) {
            toast.success("Re-insurer Removed from Premium Membership");
            refetch?.();
            setOpen?.(false);
          } else {
            toast.error("Failed to remove Re-insurer from Premium Membership");
          }
        })
        .catch((err) => {
          toast.error(
            err?.message ||
              "Failed to remove Re-insurer from Premium Membership"
          );
        });
    }
  };

  return (
    <PopUp
      open={open}
      setOpen={setOpen}
      loading={fetching}
      title={
        isPremiumMember
          ? "Remove Reinsurer from Premium Membership"
          : "Add Reinsurer to Premium Membership"
      }
      cancelText={isPremiumMember ? "Keep as Premium" : "Cancel"}
      description={
        isPremiumMember
          ? "This action would remove reinsurer details from premium membership"
          : "This action would add reinsurer details to premium membership"
      }
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading || adding}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={handleSubmit}
          >
            {loading || adding
              ? "please wait ..."
              : isPremiumMember
                ? "Remove from Premium"
                : "Add to Premium"}
          </button>
        </>
      )}
    />
  );
}
